import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../interceptors";

const WhatsAppSettings = () => {
  // Editable fields that map onto the PUT payload.
  const [form, setForm] = useState({
    phoneNumberId: "",
    accessToken: "",
    appSecret: "",
    verifyToken: "",
    businessDisplayName: "",
    wabaId: "",
    enableOutbound: true,
    enableTemplates: true,
    enableAutoReply: false,
    dailySendLimit: 1000,
  });
  // Read-only data returned by GET (status, webhook URL, secret-set flags, previews).
  const [serverConfig, setServerConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const tenantId = useMemo(
    () => Number(localStorage.getItem("tenantId")) || 1,
    [],
  );

  // Load existing config. If there is no entry (e.g. 404) we leave the form
  // untouched so nothing is populated.
  useEffect(() => {
    document.title = "WhatsApp settings - Ample Hire";
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/job-posting/api/whatsapp/${tenantId}/config`,
        );
        if (cancelled || !data) return;
        setServerConfig(data);
        setForm((f) => ({
          ...f,
          phoneNumberId: data.phoneNumberId ?? "",
          businessDisplayName: data.businessDisplayName ?? "",
          wabaId: data.wabaId ?? "",
          enableOutbound: data.enableOutbound ?? f.enableOutbound,
          enableTemplates: data.enableTemplates ?? f.enableTemplates,
          enableAutoReply: data.enableAutoReply ?? f.enableAutoReply,
          dailySendLimit: data.dailySendLimit ?? f.dailySendLimit,
          // Secrets are never returned raw — inputs stay blank.
        }));
      } catch (e) {
        // No config yet / fetch failed: keep the form empty.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const onChange = (key) => (e) => {
    const val =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: val }));
  };

  // The five fields below are required before the config can be saved. Secrets
  // (access token / app secret) count as filled when the user typed a value OR
  // one is already stored server-side, since their inputs stay blank in that case.
  const isValid = useMemo(() => {
    const accessTokenOk =
      !!form.accessToken.trim() || !!serverConfig?.accessTokenSet;
    const appSecretOk = !!form.appSecret.trim() || !!serverConfig?.appSecretSet;
    return (
      !!form.phoneNumberId.trim() &&
      !!form.businessDisplayName.trim() &&
      !!form.wabaId.trim() &&
      accessTokenOk &&
      appSecretOk
    );
  }, [form, serverConfig]);

  const save = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload = {
        phoneNumberId: form.phoneNumberId.trim(),
        businessDisplayName: form.businessDisplayName.trim(),
        wabaId: form.wabaId.trim(),
        enableOutbound: form.enableOutbound,
        enableTemplates: form.enableTemplates,
        enableAutoReply: form.enableAutoReply,
        dailySendLimit: Number(form.dailySendLimit) || 0,
      };
      // Only send secrets the user actually entered. A blank field means
      // "keep the value already stored server-side".
      if (form.accessToken.trim())
        payload.accessToken = form.accessToken.trim();
      if (form.appSecret.trim()) payload.appSecret = form.appSecret.trim();
      if (form.verifyToken.trim())
        payload.verifyToken = form.verifyToken.trim();

      const { data } = await axiosInstance.put(
        `/job-posting/api/whatsapp/${tenantId}/config`,
        payload,
      );
      if (data) setServerConfig(data);
      // Clear secret inputs after a successful save.
      setForm((f) => ({
        ...f,
        accessToken: "",
        appSecret: "",
        verifyToken: "",
      }));
      setSaveMsg({ ok: true, text: "Configuration saved." });
    } catch (e) {
      setSaveMsg({
        ok: false,
        text:
          e.response?.data?.message ||
          e.message ||
          "Failed to save configuration.",
      });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axiosInstance.post(
        "/common/otp/send-whatsapp-otp",
        { number: testPhone.trim() },
      );
      setTestResult({ ok: true, data });
    } catch (e) {
      setTestResult({
        ok: false,
        message: e.response?.data?.message || e.message,
      });
    } finally {
      setTesting(false);
    }
  };

  const status = (() => {
    const s = serverConfig?.status;
    if (s === "CONNECTED")
      return { color: "#10B981", bg: "#ECFDF5", label: "Connected" };
    if (s) return { color: "#F59E0B", bg: "#FEF3C7", label: String(s) };
    return { color: "#94A3B8", bg: "#F1F5F9", label: "Not configured" };
  })();

  const secretHint = (isSet, preview) => {
    if (isSet) {
      return `Currently set${preview ? ` (${preview})` : ""}. Leave blank to keep the existing value.`;
    }
    return "Not set yet.";
  };

  return (
    <div
      className="app-content page-enter"
      style={{ maxWidth: 760, margin: "0 auto", padding: "24px 20px" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            data-guide="whatsapp-config"
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#0F172A",
              margin: 0,
            }}
          >
            WhatsApp Business settings
          </h1>
          <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>
            Configure the Meta WhatsApp Business Cloud API integration for this
            tenant.
          </p>
        </div>
      </div>

      {/* Connection status */}
      <Section>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 5,
                background: status.color,
              }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
                {status.label}
              </div>
              <div style={{ fontSize: 11, color: "#94A3B8" }}>
                Provider: Meta WhatsApp Business Cloud API
                {serverConfig?.updatedAt && (
                  <span>
                    {" "}
                    · updated{" "}
                    {new Date(serverConfig.updatedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <span
            style={{
              fontSize: 11,
              padding: "4px 10px",
              borderRadius: 100,
              fontWeight: 600,
              background: status.bg,
              color: status.color,
            }}
          >
            {status.label.toUpperCase()}
          </span>
        </div>
      </Section>

      {/* Credentials */}
      <Section title="Meta credentials">
        <Row>
          <Field label="Phone number ID" required>
            <input
              value={form.phoneNumberId}
              onChange={onChange("phoneNumberId")}
              placeholder="1234567890123456"
              style={input}
            />
          </Field>
          <Field label="Business display name" required>
            <input
              value={form.businessDisplayName}
              onChange={onChange("businessDisplayName")}
              placeholder="Acme Hiring"
              style={input}
            />
          </Field>
        </Row>
        <Row>
          <Field label="WABA ID" required>
            <input
              value={form.wabaId}
              onChange={onChange("wabaId")}
              placeholder="9876543210"
              style={input}
            />
          </Field>
          <div />
        </Row>

        <Field label="System-user access token (EAA...)" required>
          <input
            type="password"
            value={form.accessToken}
            onChange={onChange("accessToken")}
            placeholder="••••••••••••"
            style={input}
          />
          <Hint>
            {secretHint(
              serverConfig?.accessTokenSet,
              serverConfig?.accessTokenPreview,
            )}
          </Hint>
        </Field>

        <Field label="App secret" required>
          <input
            type="password"
            value={form.appSecret}
            onChange={onChange("appSecret")}
            placeholder="••••••••••••"
            style={input}
          />
          <Hint>{secretHint(serverConfig?.appSecretSet)}</Hint>
        </Field>

        <Field label="Verify token (echoed to Meta during webhook verification)">
          <input
            type="password"
            value={form.verifyToken}
            onChange={onChange("verifyToken")}
            placeholder="••••••••"
            style={input}
          />
          <Hint>{secretHint(serverConfig?.verifyTokenSet)}</Hint>
        </Field>
      </Section>

      {/* Webhook — display only, managed server-side */}
      <Section title="Inbound webhook">
        <Field label="Webhook URL (configure this in your Meta app — read-only)">
          <input
            value={serverConfig?.webhookUrl || ""}
            readOnly
            placeholder="Available after the configuration is saved"
            style={inputReadOnly}
          />
        </Field>
        <Hint>
          This URL is generated by the server and cannot be edited. Paste it
          into the Meta App webhook settings.
        </Hint>
      </Section>

      {/* Behaviour */}
      <Section title="Behaviour">
        <Row>
          <Field label="Daily send limit">
            <input
              type="number"
              value={form.dailySendLimit}
              onChange={onChange("dailySendLimit")}
              style={input}
            />
          </Field>
          <div />
        </Row>
        <Toggle
          label="Enable outbound WhatsApp messages"
          checked={form.enableOutbound}
          onChange={onChange("enableOutbound")}
        />
        <Toggle
          label="Use approved templates for new conversations (required by Meta policy)"
          checked={form.enableTemplates}
          onChange={onChange("enableTemplates")}
        />
        <Toggle
          label="Enable AI auto-reply to inbound messages"
          checked={form.enableAutoReply}
          onChange={onChange("enableAutoReply")}
        />
      </Section>

      {/* Test */}
      <Section title="Send test message">
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <Field label="Phone number (E.164, no + sign)" style={{ flex: 1 }}>
            <input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="919876543210"
              style={input}
            />
          </Field>
          <button
            onClick={sendTest}
            disabled={testing || !testPhone.trim()}
            style={{
              ...btnPrimary,
              opacity: testing || !testPhone.trim() ? 0.5 : 1,
            }}
          >
            {testing ? "Sending…" : "Send test OTP"}
          </button>
        </div>
        {testResult && testResult.ok && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              background: "#ECFDF5",
              border: "1px solid #10B981",
              borderRadius: 6,
              fontSize: 12,
              color: "#047857",
            }}
          >
            ✓ Sent via <strong>{testResult.data?.provider || "meta"}</strong>
            {testResult.data?.response?.messages?.[0]?.id && (
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                {" "}
                · message id {testResult.data.response.messages[0].id}
              </span>
            )}
          </div>
        )}
        {testResult && !testResult.ok && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 6,
              fontSize: 12,
              color: "#991B1B",
            }}
          >
            ✗ {testResult.message}
          </div>
        )}
      </Section>

      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "white",
          padding: "12px 0",
          borderTop: "1px solid #E2E8F0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          alignItems: "center",
        }}
      >
        {saveMsg && (
          <span
            style={{ fontSize: 11, color: saveMsg.ok ? "#047857" : "#991B1B" }}
          >
            {saveMsg.ok ? "✓" : "✗"} {saveMsg.text}
          </span>
        )}
        <button
          onClick={save}
          disabled={saving || loading || !isValid}
          style={{
            ...btnPrimary,
            opacity: saving || loading || !isValid ? 0.5 : 1,
            cursor: saving || loading || !isValid ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div
    style={{
      background: "white",
      border: "1px solid #E2E8F0",
      borderRadius: 12,
      padding: 16,
      marginBottom: 14,
    }}
  >
    {title && (
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#94A3B8",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
    )}
    {children}
  </div>
);
const Row = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);
const Field = ({ label, children, style, required }) => (
  <div style={{ marginBottom: 10, ...style }}>
    <div
      style={{
        fontSize: 11,
        color: "#64748B",
        marginBottom: 4,
        fontWeight: 500,
      }}
    >
      {label}
      {required && (
        <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>
      )}
    </div>
    {children}
  </div>
);
const Hint = ({ children }) => (
  <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>{children}</div>
);
const Toggle = ({ label, checked, onChange }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 0",
      fontSize: 13,
      color: "#334155",
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={!!checked}
      onChange={onChange}
      style={{ accentColor: "#25D366" }}
    />
    {label}
  </label>
);
const input = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  outline: "none",
  background: "white",
  boxSizing: "border-box",
};
const inputReadOnly = {
  ...input,
  background: "#F8FAFC",
  color: "#64748B",
  cursor: "not-allowed",
};
const btnPrimary = {
  padding: "10px 20px",
  borderRadius: 6,
  background: "#25D366",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
};

export default WhatsAppSettings;
